import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from '../../ui/Select';
import { useNavigate, useSearchParams } from 'react-router';
import {
  LuLayoutGrid,
  LuList,
  LuPlus,
  LuSearch,
  LuDownload,
} from 'react-icons/lu';
import { toast } from 'react-toast';
import axiosInstance from '../../../api/axiosInstance';
import useProjectData from '../../../hooks/useProjectData';
import useWorkspaceRouting from '../../../hooks/useWorkspaceRouting';
import { useProject } from '../../../context/ProjectContext';
import { buildWorkspaceDashboardPath } from '../../../utils/workspaceRouteUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { useUpdateWorkspace } from '../../../hooks/useWorkspace';
import { dateFormat } from '../../../utils/dateFormat';
import { refreshProjects } from '../../../utils/projectEvents';
import { ProjectStatusBadge, ProjectTypeBadge } from '../../common';
import {
  ActionMenu,
  EmptyState,
  LoadingState,
  ErrorState,
  Pagination,
  FilterBar,
} from '../../ui';
import {
  CreateProjectModal,
  DeleteModal,
  DuplicateModal,
  EditModal,
  JiraImportModal,
  LinkProjectModal,
  LinkRepositoryModal,
} from '../../projects/Modals';
import ProjectCard from './ProjectCard';
import { deriveProjectHealth, formatPassRate, hasRepositoryLinked } from './projectHealth';
import { buildProjectMenuItems } from './projectMenu';
import '../../../styles/rt2-projects.css';

const VIEW_KEY = 'robitest_projects_view';

function readViewMode() {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

/**
 * Redesigned Projects workspace — cards/list, Open Project primary, slim overflow.
 */
export default function ProjectsExperience() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectProject } = useProject();
  const { getWorkspaceIdFromURL } = useWorkspaceRouting();
  const { mutate: updateWorkspaceFn } = useUpdateWorkspace();

  const urlSearch = searchParams.get('search') || '';
  const urlStatus = searchParams.get('status') || 'active';
  const urlPageSize = Number(searchParams.get('page_size')) || 10;
  const urlSort = searchParams.get('sort') || '';
  const urlSortDir = searchParams.get('sort_direction') || 'desc';

  const [viewMode, setViewMode] = useState(readViewMode);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 400);

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [linkProject, setLinkProject] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectType, setProjectType] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editType, setEditType] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [repoLinkProject, setRepoLinkProject] = useState(null);
  const [repoLinkEditMode, setRepoLinkEditMode] = useState(false);
  const [repoLinkInitialData, setRepoLinkInitialData] = useState(null);

  const {
    filteredProjects,
    isLoading,
    error,
    currentPage,
    totalPages,
    pageSize,
    activeTab,
    statusCounts,
    isBitbucketConfigured,
    setCurrentPage,
    setPageSize,
    setActiveTab,
    setSearchTerm,
    fetchProjects,
    updateProjectStatus,
    deleteProject,
    duplicateProject,
    updateProject,
  } = useProjectData(1, urlStatus, urlPageSize);

  const patchParams = useCallback(
    (patch, { resetPage = false } = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v == null || v === '' || (k === 'status' && v === 'active') || (k === 'page_size' && Number(v) === 10)) {
              if (k === 'status' && v === 'active') next.delete(k);
              else if (k === 'page_size' && Number(v) === 10) next.delete(k);
              else if (v == null || v === '') next.delete(k);
              else next.set(k, String(v));
            } else {
              next.set(k, String(v));
            }
          });
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Sync local search → URL
  useEffect(() => {
    if (debouncedSearch === urlSearch) return;
    patchParams({ search: debouncedSearch.trim() }, { resetPage: true });
  }, [debouncedSearch, urlSearch, patchParams]);

  // Keep input in sync when navigating back/forward
  useEffect(() => {
    setSearchInput(urlSearch);
  }, [urlSearch]);

  // Sync status / page size from URL into hook
  useEffect(() => {
    if (urlStatus !== activeTab) setActiveTab(urlStatus);
  }, [urlStatus, activeTab, setActiveTab]);

  useEffect(() => {
    if (urlPageSize !== pageSize) setPageSize(urlPageSize);
  }, [urlPageSize, pageSize, setPageSize]);

  useEffect(() => {
    setSearchTerm(urlSearch);
  }, [urlSearch, setSearchTerm]);

  const sortConfig = useMemo(() => {
    if (!urlSort) return null;
    const sortTypeMap = { name: 1, project_name: 1, created: 2, updated: 2 };
    const sort_type = sortTypeMap[urlSort];
    if (!sort_type) return null;
    return {
      key: urlSort,
      direction: urlSortDir === 'asc' ? 'asc' : 'desc',
      sort_type,
      sort_order: urlSortDir === 'asc' ? 1 : 2,
    };
  }, [urlSort, urlSortDir]);

  useEffect(() => {
    fetchProjects(currentPage, urlStatus, urlSearch, sortConfig, pageSize);
  }, [fetchProjects, currentPage, urlStatus, urlSearch, sortConfig, pageSize]);

  const setView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const openProject = useCallback(
    (project) => {
      if (!project?.id) return;
      const workspaceId = getWorkspaceIdFromURL();
      selectProject(String(project.id), project.name);
      if (workspaceId) {
        updateWorkspaceFn({
          last_active_workspace_id: workspaceId,
          last_active_project_id: project.id,
        });
        navigate(buildWorkspaceDashboardPath(workspaceId, project.id));
      } else {
        navigate('/dashboard');
      }
      toast.success(`Opened “${project.name}”`);
    },
    [getWorkspaceIdFromURL, navigate, selectProject, updateWorkspaceFn]
  );

  const openProjectRoute = useCallback(
    (project, subPath, state) => {
      if (!project?.id || !subPath) return;
      const workspaceId = getWorkspaceIdFromURL();
      selectProject(String(project.id), project.name);
      if (workspaceId) {
        updateWorkspaceFn({
          last_active_workspace_id: workspaceId,
          last_active_project_id: project.id,
        });
      }
      navigate(
        workspaceId
          ? buildWorkspaceDashboardPath(workspaceId, project.id, subPath)
          : `/dashboard/${subPath}`,
        state ? { state } : undefined
      );
    },
    [getWorkspaceIdFromURL, navigate, selectProject, updateWorkspaceFn]
  );

  const handleCrawlProject = useCallback(
    (project) => {
      openProjectRoute(project, `project-crawl/${project.id}`);
    },
    [openProjectRoute]
  );

  const handleIntegration = useCallback((project) => {
    setLinkProject(project);
  }, []);

  const handleArchive = async (project) => {
    const ok = await updateProjectStatus(project.id, 'archived');
    if (ok) toast.success(`Archived “${project.name}”`);
  };

  const handleUnarchive = async (project) => {
    const ok = await updateProjectStatus(project.id, 'active');
    if (ok) toast.success(`Activated “${project.name}”`);
  };

  const handleActivate = async (project) => {
    const ok = await updateProjectStatus(project.id, 'active');
    if (ok) toast.success(`Activated “${project.name}”`);
  };

  const handleCreateProject = async () => {
    try {
      setCreateLoading(true);
      const workspaceId = getWorkspaceIdFromURL();
      const response = await axiosInstance.post('/projects/add_project', {
        project_name: projectName,
        description: projectDescription,
        project_type: projectType,
        status: 'active',
        workspace_id: workspaceId ? Number(workspaceId) : null,
      });
      if (response.status_code === 201 || response?.project) {
        toast.success('Project created');
        setCreateOpen(false);
        setProjectName('');
        setProjectDescription('');
        setProjectType('');
        fetchProjects(1, urlStatus, urlSearch, sortConfig);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create project');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDuplicateConfirm = async (newName) => {
    if (!duplicateTarget) return;
    const created = await duplicateProject(duplicateTarget.id, newName);
    if (created) {
      toast.success(`Duplicated as “${newName}”`);
      setDuplicateTarget(null);
      fetchProjects(currentPage, urlStatus, urlSearch, sortConfig);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const ok = await deleteProject(deleteTarget.id);
    if (ok) {
      setDeleteTarget(null);
      fetchProjects(currentPage, urlStatus, urlSearch, sortConfig);
      refreshProjects();
    }
  };

  const openEdit = (project) => {
    setEditTarget(project);
    setEditName(project.name || '');
    setEditDescription(project.description || '');
    setEditType(project.type || '');
    setEditStatus(project.status || 'active');
  };

  const openLinkRepository = (project) => {
    setRepoLinkProject(project);
    setRepoLinkEditMode(false);
    setRepoLinkInitialData(null);
  };

  const openUpdateRepository = async (project) => {
    try {
      const response = await axiosInstance.get(`/get_project_repo?project_id=${project.id}`);
      if (response) {
        setRepoLinkProject(project);
        setRepoLinkInitialData(response);
        setRepoLinkEditMode(true);
      } else {
        toast.error('Failed to fetch repository details');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch repository details');
    }
  };

  const handleRepositoryAction = (project) => {
    if (!isBitbucketConfigured) {
      toast.error('Connect Bitbucket in Settings before linking a repository.');
      return;
    }
    if (hasRepositoryLinked(project)) {
      openUpdateRepository(project);
    } else {
      openLinkRepository(project);
    }
  };

  const closeRepoModal = () => {
    setRepoLinkProject(null);
    setRepoLinkEditMode(false);
    setRepoLinkInitialData(null);
  };

  const handleRepoLinkSuccess = async () => {
    await fetchProjects(currentPage, urlStatus, urlSearch, sortConfig, pageSize);
  };

  const handleEditSave = async (projectId, updatedData) => {
    const id = projectId || editTarget?.id;
    if (!id) return;
    const payload = updatedData || {
      name: editName,
      description: editDescription,
      type: editType,
      status: editStatus,
    };
    const ok = await updateProject(id, {
      name: payload.name ?? editName,
      description: payload.description ?? editDescription,
      type: payload.type ?? editType,
      status: payload.status ?? editStatus,
    });
    if (ok) {
      toast.success('Project updated');
      setEditTarget(null);
      fetchProjects(currentPage, urlStatus, urlSearch, sortConfig, pageSize);
    }
  };

  const menuFor = (project) =>
    buildProjectMenuItems(project, {
      bitbucketConfigured: isBitbucketConfigured,
      onEdit: openEdit,
      onIntegration: handleIntegration,
      onLinkRepo: handleRepositoryAction,
      onCrawl: handleCrawlProject,
      onDuplicate: setDuplicateTarget,
      onArchive: handleArchive,
      onUnarchive: handleUnarchive,
      onActivate: handleActivate,
      onDelete: setDeleteTarget,
    });

  const totalEstimate = useMemo(() => {
    if (urlStatus === 'all') {
      return (
        (statusCounts.active || 0) +
        (statusCounts.inactive || 0) +
        (statusCounts.archived || 0)
      );
    }
    return statusCounts[urlStatus] ?? filteredProjects.length;
  }, [statusCounts, urlStatus, filteredProjects.length]);

  return (
    <div className="rt-projects">
      <div className="rt-projects__toolbar">
        <FilterBar>
          <div className="rt-projects__search">
            <LuSearch size={16} aria-hidden />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search projects…"
              aria-label="Search projects"
            />
          </div>

          <Select
            className="rt-select--filter"
            value={urlStatus}
            aria-label="Filter by status"
            onChange={(e) => patchParams({ status: e.target.value }, { resetPage: true })}
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All statuses</option>
          </Select>

          <Select
            className="rt-select--filter"
            value={urlSort || 'created'}
            aria-label="Sort projects"
            onChange={(e) => {
              const sort = e.target.value;
              patchParams(
                { sort, sort_direction: sort === 'name' ? 'asc' : 'desc' },
                { resetPage: true }
              );
            }}
          >
            <option value="created">Sort: Created</option>
            <option value="name">Sort: Name</option>
          </Select>

          <div className="rt-projects__view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={viewMode === 'grid' ? 'is-active' : ''}
              aria-pressed={viewMode === 'grid'}
              title="Card view"
              onClick={() => setView('grid')}
            >
              <LuLayoutGrid size={15} />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'is-active' : ''}
              aria-pressed={viewMode === 'list'}
              title="List view"
              onClick={() => setView('list')}
            >
              <LuList size={15} />
            </button>
          </div>

          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={() => setImportOpen(true)}
          >
            <LuDownload size={16} /> Import Projects
          </button>
          <button
            type="button"
            className="rt-btn rt-btn--primary"
            onClick={() => setCreateOpen(true)}
          >
            <LuPlus size={16} /> Create Project
          </button>
        </FilterBar>
      </div>

      {error && (
        <ErrorState
          title="Unable to load projects"
          description={error}
          onRetry={() => fetchProjects(currentPage, urlStatus, urlSearch, sortConfig)}
        />
      )}

      {!error && isLoading && filteredProjects.length === 0 && (
        <LoadingState label="Loading projects…" />
      )}

      {!error && !isLoading && filteredProjects.length === 0 && (
        <EmptyState
          title={urlSearch ? 'No matching projects' : 'No projects yet'}
          description={
            urlSearch
              ? `Nothing matched “${urlSearch}”. Try another search or clear filters.`
              : 'Create a project or import one from Jira or WorkXiq.'
          }
          action={
            !urlSearch ? (
              <div className="rt-projects__empty-actions">
                <button type="button" className="rt-btn rt-btn--secondary" onClick={() => setImportOpen(true)}>
                  <LuDownload size={16} /> Import Projects
                </button>
                <button type="button" className="rt-btn rt-btn--primary" onClick={() => setCreateOpen(true)}>
                  <LuPlus size={16} /> Create Project
                </button>
              </div>
            ) : null
          }
        />
      )}

      {!error && filteredProjects.length > 0 && viewMode === 'grid' && (
        <div className="rt-proj-grid">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpen={openProject}
              onEdit={openEdit}
              onIntegration={handleIntegration}
              onLinkRepo={handleRepositoryAction}
              onCrawl={handleCrawlProject}
              bitbucketConfigured={isBitbucketConfigured}
              onDuplicate={setDuplicateTarget}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onActivate={handleActivate}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      {!error && filteredProjects.length > 0 && viewMode === 'list' && (
        <div className="rt-proj-table-wrap">
          <table className="rt-proj-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Type</th>
                <th>Status</th>
                <th>Tests</th>
                <th>Pass rate</th>
                <th>Health</th>
                <th>Updated</th>
                <th className="rt-proj-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => {
                const health = deriveProjectHealth(project);
                const passRate = formatPassRate(project);
                return (
                  <tr key={project.id}>
                    <td>
                      <button
                        type="button"
                        className="rt-proj-table__name"
                        onClick={() => openProject(project)}
                      >
                        {project.name}
                      </button>
                      {project.description ? (
                        <div className="rt-proj-table__desc">{project.description}</div>
                      ) : null}
                    </td>
                    <td>
                      <ProjectTypeBadge type={project.type} />
                    </td>
                    <td>
                      <ProjectStatusBadge status={project.status} />
                    </td>
                    <td>{Number(project.testCases || 0).toLocaleString()}</td>
                    <td>{passRate ?? '—'}</td>
                    <td>
                      <span className={`rt-proj-health rt-proj-health--${health.tone}`}>
                        <span className="rt-proj-health__dot" aria-hidden />
                        {health.label}
                      </span>
                    </td>
                    <td>{project.updatedAt ? dateFormat(project.updatedAt) : '—'}</td>
                    <td className="rt-proj-table__actions">
                      <div className="rt-row-actions">
                        <button
                          type="button"
                          className="rt-btn rt-btn--secondary rt-btn--sm"
                          onClick={() => openProject(project)}
                        >
                          Open
                        </button>
                        <ActionMenu items={menuFor(project)} label={`Actions for ${project.name}`} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(totalPages > 1 || filteredProjects.length > 0) && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          total={totalEstimate}
          totalPages={totalPages}
          loading={isLoading}
          onPageChange={(p) => setCurrentPage(p)}
          onPageSizeChange={(size) => {
            const nextSize = Number(size) || 10;
            setPageSize(nextSize);
            patchParams({ page_size: nextSize }, { resetPage: true });
            fetchProjects(1, urlStatus, urlSearch, sortConfig, nextSize);
          }}
        />
      )}

      {createOpen && (
        <CreateProjectModal
          toggleCreateProjectModal={() => setCreateOpen(false)}
          projectName={projectName}
          setProjectName={setProjectName}
          projectDescription={projectDescription}
          setProjectDescription={setProjectDescription}
          projectType={projectType}
          setProjectType={setProjectType}
          handleCreateProject={handleCreateProject}
          loading={createLoading}
        />
      )}

      <DuplicateModal
        isOpen={Boolean(duplicateTarget)}
        onClose={() => setDuplicateTarget(null)}
        project={duplicateTarget}
        onDuplicate={handleDuplicateConfirm}
      />

      {deleteTarget && (
        <DeleteModal
          projectToDelete={deleteTarget}
          setIsDeleteModalOpen={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          setProjectToDelete={setDeleteTarget}
          handleDeleteProject={handleDeleteConfirm}
        />
      )}

      {editTarget && (
        <EditModal
          selectedProject={editTarget}
          setIsEditModalOpen={(open) => {
            if (!open) setEditTarget(null);
          }}
          editName={editName}
          setEditName={setEditName}
          editDescription={editDescription}
          setEditDescription={setEditDescription}
          editProjectType={editType}
          setEditProjectType={setEditType}
          editProjectStatus={editStatus}
          setEditProjectStatus={setEditStatus}
          handleUpdateProject={handleEditSave}
          isLoading={isLoading}
        />
      )}

      <LinkRepositoryModal
        isOpen={Boolean(repoLinkProject)}
        onClose={closeRepoModal}
        onSuccess={handleRepoLinkSuccess}
        projectId={repoLinkProject?.id}
        isEditMode={repoLinkEditMode}
        initialData={repoLinkInitialData}
      />

      <JiraImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={() => fetchProjects(currentPage, urlStatus, urlSearch, sortConfig, pageSize)}
      />

      <LinkProjectModal
        isOpen={Boolean(linkProject)}
        onClose={() => setLinkProject(null)}
        projectId={linkProject?.id}
        onLinkSuccess={() => fetchProjects(currentPage, urlStatus, urlSearch, sortConfig, pageSize)}
      />
    </div>
  );
}
